import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../util/api";
import { useLocation, useParams } from "react-router-dom";
import ActionButton from "../../../component/common/ActionButton";

function normalizePhone(raw) {
    if (!raw) return "";
    // vCard TEL은 기기별 호환을 위해 숫자/+만 남김(하이픈 제거)
    return String(raw).trim().replace(/[^\d+]/g, "");
}

function buildVCard({ name, email, phone }) {
    const safeName = (name || "").trim();
    const safeEmail = (email || "").trim();
    const safePhone = normalizePhone(phone);

    return [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${safeName}`,
        safeEmail ? `EMAIL;TYPE=INTERNET:${safeEmail}` : "",
        safePhone ? `TEL;TYPE=CELL:${safePhone}` : "",
        "END:VCARD",
    ]
        .filter(Boolean)
        .join("\r\n");
}

function detectOS() {
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    return { isAndroid, isIOS };
}

export default function BzCardLanding() {
    const params = useParams(); // /bzcard=:code
    const location = useLocation();

    // /bzcard?code=... 지원
    const queryCode = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        return sp.get("code") || "";
    }, [location.search]);

    const code = useMemo(() => {
        return (params.code || queryCode || "").trim();
    }, [params.code, queryCode]);

    const { isAndroid, isIOS } = useMemo(() => detectOS(), []);

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null); // {name,email,phone}
    const [error, setError] = useState(null);

    const API_BASE = process.env.REACT_APP_API_REAL_URL;

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!code) {
                setError("개인코드가 없습니다. 링크를 다시 확인해주세요.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const resData = await api.post(
                    `${API_BASE}/public/qr-event-participant/bizcard`,
                    { code }
                );

                if (cancelled) return;

                const data = resData || {};
                setInfo({
                    name: data.name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                });
            } catch (e) {
                if (cancelled) return;
                setInfo(null);
                setError("정보를 불러오지 못했습니다. 코드가 유효한지 확인해주세요.");


            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [API_BASE, code]);

    const handleDownload = () => {
        if (!info) return;

        const vcf = buildVCard(info);
        const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;

        // 코드 노출 X: 파일명은 이름 기반
        const fileNameBase = (info.name || "contact").replace(/[\\/:*?"<>|]/g, "_");
        a.download = `${fileNameBase}.vcf`;

        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const guideTitle = useMemo(() => {
        if (isIOS) return "iPhone 사용 방법";
        if (isAndroid) return "Android 사용 방법";
        return "사용 방법";
    }, [isAndroid, isIOS]);

    const guideBody = useMemo(() => {
        if (isIOS) {
            return (
                <>
                    1) <b>연락처 저장</b> 버튼을 누르세요.
                    <br />
                    2) 연락처 화면이 열리면 <b>추가/저장</b>을 누르면 됩니다.
                </>
            );
        }
        if (isAndroid) {
            return (
                <>
                    1) <b>연락처 저장</b> 버튼을 누르면 <b>.vcf</b> 파일이 다운로드됩니다.
                    <br />
                    2) 상단 알림(다운로드 완료)을 탭하거나 <b>다운로드</b> 폴더에서 파일을 여세요.
                    <br />
                    3) “연락처/주소록으로 열기”를 선택한 뒤 <b>저장</b>하면 됩니다.
                    <div style={{ marginTop: 8, color: "#6b7280", fontSize: 12, lineHeight: 1.5 }}>
                        * Android 브라우저는 정책상 iPhone처럼 자동으로 연락처 화면을 바로 여는 동작이 제한되는 경우가 많아요.
                    </div>
                </>
            );
        }
        return (
            <>
                <b>연락처 저장</b> 버튼을 누른 뒤, 다운로드된 <b>.vcf</b> 파일을 열어 저장하세요.
            </>
        );
    }, [isAndroid, isIOS]);

    // ✅ 모바일 최적화 UI (PC에서도 보기 좋게 “카드” 레이아웃 유지)
    return (
        <div
            style={{
                minHeight: "100vh",
                padding: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#ffffff",
                fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: 520, // PC에서도 너무 좁지 않게
                    border: "1px solid #e5e7eb",
                    borderRadius: 18,
                    padding: 18,
                    boxShadow: "0 10px 26px rgba(0,0,0,0.08)",
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>명함 저장</div>
                    <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.4 }}>
                        버튼을 눌러 주소록에 추가하세요.
                    </div>
                </div>

                {loading && <div style={{ color: "#6b7280" }}>자료를 불러오고 있어요...</div>}

                {!loading && error && (
                    <div
                        style={{
                            color: "#b91c1c",
                            background: "#fef2f2",
                            border: "1px solid #fecaca",
                            padding: 12,
                            borderRadius: 12,
                            lineHeight: 1.5,
                        }}
                    >
                        {error}
                    </div>
                )}

                {!loading && info && (
                    <>
                        <div style={{ display: "grid", gap: 12, marginBottom: 14 }}>
                            <div style={{ padding: 12, borderRadius: 14, background: "#f9fafb" }}>
                                <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>이름</div>
                                <div style={{ fontSize: 16, fontWeight: 800 }}>{info.name || "-"}</div>
                            </div>

                            <div style={{ padding: 12, borderRadius: 14, background: "#f9fafb" }}>
                                <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>이메일</div>
                                <div style={{ fontSize: 14 }}>
                                    {info.email ? (
                                        <a href={`mailto:${info.email}`} style={{ color: "#2563eb", wordBreak: "break-all" }}>
                                            {info.email}
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>

                            <div style={{ padding: 12, borderRadius: 14, background: "#f9fafb" }}>
                                <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 4 }}>전화번호</div>
                                <div style={{ fontSize: 14 }}>
                                    {info.phone ? (
                                        <a href={`tel:${info.phone}`} style={{ color: "#2563eb" }}>
                                            {info.phone}
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>
                        </div>

                        <ActionButton
                            variant="primary"
                            startIcon={false}
                            onClick={handleDownload}
                            sx={{
                                width: "100%",
                                height: 48,
                                borderRadius: "14px",
                                fontWeight: 700,
                                fontSize: 15,
                                bgcolor: "#111827",
                                "&:hover": { bgcolor: "#000" },
                            }}
                            label="연락처 저장"
                        />

                        <div
                            style={{
                                marginTop: 14,
                                padding: 12,
                                borderRadius: 14,
                                background: "#f3f4f6",
                                fontSize: 13,
                                lineHeight: 1.55,
                            }}
                        >
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>{guideTitle}</div>
                            <div>{guideBody}</div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}